import {
  Button,
  Card,
  Textarea,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  Spinner,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

import useSegment from '@/hooks/useSegment';
import { Education } from '@/patient-app-common/types/Education';
import { educationTopic } from '@/patient-app-common/helpers/education_helpers';

import styles from '../../styles/education.module.css';

import { ChatbotFeedback } from './ChatbotFeedback';
import { ChatbotInfo } from './ChatbotInfo';
import chatbotStyles from './styles/chatbot.module.css';
import { ChatbotError } from './ChatbotError';

export function ChatbotContent({
  containerRef,
  handleSubmit,
  submitFeedback,
  setQuery,
  queryRef,
  query,
  isLoading,
  isHistoryLoading,
  isChatSendDisabled,
  isError,
  hasAiConsent,
  responseHistory,
  education,
  pageWidth,
  feedback,
  isLoadingMoreHistory,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  handleSubmit: (query?: string) => void;
  submitFeedback: (id: string, helpful: boolean, comment?: string) => void;
  setQuery: (value: string) => void;
  queryRef: React.RefObject<HTMLDivElement | null>;
  query: string;
  isLoading: boolean;
  isHistoryLoading: boolean;
  isChatSendDisabled: boolean;
  isError: boolean;
  hasAiConsent: boolean;
  responseHistory: Array<{
    id: string;
    content: string;
    chunks: { order: number; content: string }[];
    complete?: boolean;
    error?: boolean;
    hidden?: boolean;
  }>;
  education: Education[];
  pageWidth: number;
  feedback: {
    [id: string]: {
      helpful: boolean | null;
      comment: string | null;
    };
  };
  isLoadingMoreHistory: boolean;
}) {
  const { t } = useTranslation('education');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerContent, setDrawerContent] = useState('');

  const visibleHistory = responseHistory.filter((r) => r.hidden !== true);
  const { sendTrackEvent } = useSegment();
  const AI_EDUCATION_LINK_PRESS = 'Education AI citation clicked';
  const AI_EDUCATION_TOPIC_VIEWED = 'Education Topic Viewed';

  useEffect(() => {
    if (drawerOpen && drawerContent !== '') {
      sendTrackEvent(AI_EDUCATION_TOPIC_VIEWED, { source: 'AI Citation' });
    }
  }, [drawerOpen, drawerContent, sendTrackEvent]);

  const handleChatbotClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target;
    if (!(target instanceof HTMLAnchorElement)) return;

    const href = target.getAttribute('href');
    if (!href) return;

    let url: URL;
    try {
      url = new URL(href, window.location.origin);
    } catch {
      return;
    }
    if (url.host === window.location.host) {
      const match = href?.match(/[#/]education\/(\d+)/);

      if (match) {
        const topic = educationTopic(education, match[1]);
        const html = topic?.html ? topic.html : '';
        e.preventDefault();
        setDrawerContent(html);
        setDrawerOpen(true);
        sendTrackEvent(AI_EDUCATION_LINK_PRESS, { title: topic?.title });
      }
    }
  };

  return (
    <main className={styles.mainViewport}>
      <div
        className={`${_.isEmpty(responseHistory) ? chatbotStyles.landingpageContainer : chatbotStyles.chatbotContainer} focus:outline-none`}
        ref={containerRef}
        tabIndex={0}
        onClick={handleChatbotClick}
      >
        {isLoadingMoreHistory && (
          <div className="text-xs text-default-500 text-center py-2">
            {t('chatbotcontent.loading_text')}
          </div>
        )}
        {!_.isEmpty(visibleHistory) ? (
          <>
            {visibleHistory.map((response, i) => {
              const questionChunk = response.chunks?.[0];
              const answerChunks = response.chunks?.slice(1) || [];
              const hasAnswer = answerChunks.length > 0;
              const hasError = response.error;

              return (
                <div
                  key={response.id}
                  className={`ed-v3 ${chatbotStyles.chatbotResponse}`}
                >
                  {questionChunk && (
                    <div
                      ref={
                        i === visibleHistory.length - 1 && !isLoading
                          ? queryRef
                          : undefined
                      }
                    >
                      <AnimatePresence mode="popLayout">
                        <motion.div
                          key={`${response.id}-question-${i}`}
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          dangerouslySetInnerHTML={{
                            __html: questionChunk.content,
                          }}
                        />
                      </AnimatePresence>
                    </div>
                  )}

                  {hasAnswer && !hasError ? (
                    <>
                      <ReplyHeader />
                      <div className={chatbotStyles.answerWrapper}>
                        <AnimatePresence mode="popLayout">
                          {answerChunks.map((chunk, chunkIndex) => (
                            <motion.div
                              key={`${response.id}-answer-${chunkIndex}-${i}`}
                              initial={{ opacity: 0, y: 4 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0 }}
                              transition={{ duration: 0.25 }}
                              dangerouslySetInnerHTML={{
                                __html: chunk.content,
                              }}
                            />
                          ))}
                        </AnimatePresence>
                        <ChatbotFeedback
                          responseId={response.id || ''}
                          submitFeedback={submitFeedback}
                          feedback={feedback[response.id]}
                        />
                      </div>
                    </>
                  ) : (
                    <ChatbotError
                      handleSubmit={handleSubmit}
                      queryRef={queryRef}
                      isLoading={isLoading}
                      querySendError={isError}
                      chatbotResponseError={hasError}
                      previousQuery={questionChunk.content}
                    />
                  )}
                </div>
              );
            })}
            <EduTopicDrawer
              isOpen={drawerOpen}
              onClose={() => {
                setDrawerOpen(false);
                setDrawerContent('');
              }}
              content={drawerContent}
              pageWidth={pageWidth}
            />
            {isLoading && (
              <div ref={queryRef} className={chatbotStyles.chatbotResponse}>
                <ReplyHeader />
                <div className={chatbotStyles.answerWrapper}>
                  <div className={chatbotStyles.loadingText}>
                    <span>{t('chatbotcontent.loader.title')}</span>
                    <div className={chatbotStyles.typingIndicator}>
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                  <div className={chatbotStyles.loadingSource}>
                    <svg
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
                      />
                    </svg>
                    <span>{t('chatbotcontent.loader.text')}</span>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {isHistoryLoading ? (
              <div className="items-center flex flex-col justify-center">
                <Spinner className="m-4" color="primary" size="lg" />
                <h1 className="text-lg">{t('common.text.loading')}</h1>
              </div>
            ) : (
              <ChatbotInfo pageWidth={pageWidth} />
            )}
          </>
        )}
        <div />
      </div>

      <ChatbotInputArea
        hasAiConsent={hasAiConsent}
        isError={isError}
        isLoading={isLoading}
        isChatSendDisabled={isChatSendDisabled}
        query={query}
        setQuery={setQuery}
        handleSubmit={handleSubmit}
        hasHistory={!_.isEmpty(responseHistory)}
      />
    </main>
  );
}

function EduTopicDrawer({
  isOpen,
  onClose,
  content,
  pageWidth,
}: {
  isOpen: boolean;
  onClose: () => void;
  content: string;
  pageWidth: number;
}) {
  const { t } = useTranslation('education');
  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      className={styles.drawerContentAuto}
      size={pageWidth <= 1023 ? 'full' : 'lg'}
      radius={pageWidth <= 1023 ? 'none' : 'md'}
      hideCloseButton={true}
    >
      <DrawerContent>
        <DrawerHeader className="flex flex-row justify-between items-center pr-0">
          <span>{t('chatbotcontent.drawer.title')}</span>
          <Button onPress={onClose} className="text-xl ml-auto" variant="light">
            <Icon icon="tabler:x" />
          </Button>
        </DrawerHeader>
        <hr
          style={{
            border: 'none',
            borderBottom: '1px solid #e5e7eb',
            margin: '0px 0',
          }}
        />
        <DrawerBody>
          {isOpen && content !== '' && (
            <div
              className="ed-v3"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

function ChatbotInputArea({
  hasAiConsent,
  isError,
  isLoading,
  isChatSendDisabled,
  query,
  setQuery,
  handleSubmit,
  hasHistory,
}: {
  hasAiConsent: boolean;
  isError: boolean;
  isLoading: boolean;
  isChatSendDisabled: boolean;
  query: string;
  setQuery: (value: string) => void;
  handleSubmit: (query?: string) => void;
  hasHistory: boolean;
}) {
  const { t } = useTranslation('education');
  const characterLimit = 500;
  return (
    <div
      className={`${hasHistory ? 'bg-default-100' : 'bg-transparent'} mt-auto w-full`}
    >
      <div className={`bottom-0 sticky mx-4 mb-4 flex-none`}>
        <Textarea
          className="w-full"
          color="primary"
          classNames={{ inputWrapper: 'bg-background' }}
          isInvalid={isError}
          label={t('chatbotcontent.input.placeholder_text')}
          maxLength={characterLimit}
          onChange={(e: React.FormEvent<HTMLInputElement>) =>
            setQuery(e.currentTarget.value)
          }
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (isChatSendDisabled) return;
              handleSubmit();
            }
          }}
          value={query}
          variant="bordered"
        />
        <div className="flex flex-row gap-6">
          <div className="mt-auto">
            {hasAiConsent && (
              <AnimatePresence>
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 40 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                  key="ai-warning"
                >
                  <Card
                    className="bg-warning-200 my-3"
                    radius="sm"
                    shadow="none"
                  >
                    <p className="m-2 text-warning-900 text-xs" tabIndex={0}>
                      {t('chatbotcontent.disclaimer_text')}
                    </p>
                  </Card>
                </motion.div>
              </AnimatePresence>
            )}
          </div>
          <div className="flex flex-col items-end gap-1 ml-auto">
            <div
              className={`flex justify-end text-xs ${
                query.length >= characterLimit
                  ? 'text-danger-500'
                  : 'text-default-400'
              }`}
            >
              <span>
                {query.length}/{characterLimit}
              </span>
            </div>
            <Button
              className="ml-auto"
              color="primary"
              isDisabled={isChatSendDisabled}
              isLoading={isLoading}
              variant="solid"
              onPress={() => handleSubmit()}
            >
              <span>{t('common.action.send')}</span>
              <Icon icon="tabler:send-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReplyHeader() {
  const { t } = useTranslation();

  return (
    <div className={chatbotStyles.aiHeader}>
      <div className={chatbotStyles.aiAvatar}>
        <Icon icon="heroicons:sparkles" className="w-4 h-4" />
      </div>
      <span className={chatbotStyles.aiName}>{t('common.link.answers')}</span>
    </div>
  );
}
